import React, { useState, useEffect, useRef, useContext } from 'react';

import { CatalogContext } from 'src/pages/CatalogPage/CatalogPage';
import { formatCurrency, sanitizeWithDashes } from 'src/lib/catalog';
import { AuctionItemActions } from 'src/pages/CatalogPage/components/AuctionItems/AuctionItem/AuctionItemActions/AuctionItemActions';
import { ItemIcons } from 'src/pages/CatalogPage/components/AuctionItems/AuctionItem/ItemIcons/ItemIcons';
import {
  Description,
  Item,
  LotDetail,
  LotImage,
  LotImageObject,
  LotProperty,
  LotTitle,
  MultiItemOverlay,
  NRAFFF,
} from 'src/pages/CatalogPage/components/AuctionItems/AuctionItem/AuctionItem.style';
import { TextButton } from '@riac/design-system';
import { Button } from '@riac/design-system';
import { ScrollContext } from 'src/lib/ScrollContext';

import { IAuctionItem } from 'src/pages/CatalogPage/CatalogPage.i';

import { ImgLazyLoader, LazyImg } from '@riac/design-system';

import NRAFFFLogo from 'images/NRAFFF LOGO-2.png';
import { getIsPricelessLot } from 'src/lib/getIsPricelessLot';
import { LoginToUse } from 'src/components/LoginToUse/LoginToUse';

interface IAuctionItemComponent {
  item: IAuctionItem;
  showPricesRealized: 0 | 1;
}

export const AuctionItem = (props: IAuctionItemComponent) => {
  const { headerPadding } = React.useContext(ScrollContext);
  const itemRef = useRef<HTMLLIElement>(null);
  const { auctionId, auctionStatusId, compactMode, auctionLive, activeLot, auctionTypeId } = useContext(CatalogContext);

  const saniwebtitle = sanitizeWithDashes(props.item.WebTitle);
  const itemLink = `/detail/${auctionId}/${props.item.LotNumber}/${saniwebtitle}`;
  const shortDesc = props.item.DetailDesc ? props.item.DetailDesc.substring(0, 50) : '';

  const [readMore, setReadMore] = useState(false);

  function togglereadmore() {
    setReadMore(!readMore);
  }

  const updateHistoryState = (partialState: Record<string, any>) => {
    window.history.pushState(Object.assign(
      {},
      window.history.state,
      partialState,
    ), window.document.title);
  };

  useEffect(() => {
    if(window?.history?.state?.returnLot == props.item.LotNumber) {
      const targetElemRect = itemRef?.current.getBoundingClientRect();
      window.scrollTo(0, targetElemRect.top - headerPadding + (window.scrollY || window.pageYOffset));
      updateHistoryState({ returnLot: (-1) });
    }
  }, [itemRef.current]);

  const completedLot = auctionTypeId !== TIMED_AUCTION_TYPE_ID && auctionLive && activeLot > props.item.LotNumber;

  return (
    <Item
      key={props.item.AuctionItemID}
      id={`LotNumber${props.item.LotNumber}`}
      compact={compactMode === 'true'}
      ref={itemRef}
    >
      <LotImage>
        <Button
          to={itemLink}
          onPress={() => updateHistoryState({ returnLot: props.item.LotNumber })}
        >
          <ImgLazyLoader>
            <LotImageObject type='image/jpeg' data={`${window.CDN_PATH}${props.item.AuctionID}/255X159/${props.item.Photo}`}>
              <img src={`${window.CDN_PATH}notfound_255X159.png`} alt={(props?.item?.Headline && props?.item?.Headline[0]) ? props?.item?.Headline[0] : ''} />
            </LotImageObject>
          </ImgLazyLoader>
          {props.item.LotItemCount > 1 && (
            <MultiItemOverlay compact={compactMode === 'true'}>
              <p>
                <span>Lot Contains </span>
                <span>{props.item.LotItemCount} Items</span>
              </p>
            </MultiItemOverlay>
          )}
          {(props.item.NRAID && props.item.NRAID !== 'false'
            && props.item.NRAID !== '') && <NRAFFF src={NRAFFFLogo} alt='NRAFFF LOGO' /> || ''}
        </Button>
      </LotImage>
      <Description>
        <TextButton
          to={itemLink}
          onPress={() => updateHistoryState({ returnLot: props.item.LotNumber })}
        >
          <LotTitle>
            Lot {props.item.LotNumber}: {props.item.WebTitle}
          </LotTitle>
        </TextButton>
        {compactMode !== 'true' && <LotDetail>{props?.item?.Headline}</LotDetail>}
        <br />
        {(auctionTypeId !== TIMED_AUCTION_TYPE_ID)
        && <LotDetail>
          <LotProperty>Estimated Price: </LotProperty>
          {
            getIsPricelessLot(props?.item?.AuctionID, props?.item?.LotNumber) ? 'Priceless'
              : (
                (props?.item?.LowEst && props?.item?.HighEst)
                  ? `${formatCurrency(props.item.LowEst)} - ${formatCurrency(props.item.HighEst)}`
                  : 'Not Available'
              )
          }
        </LotDetail>
        }
        {props.item.Guage && (
          <LotDetail>
            <LotProperty>Caliber / Gauge: </LotProperty>
            {props.item.Guage}
          </LotDetail>
        )}
        {compactMode !== 'true' && (
          <>
            <ItemIcons item={props.item} />
            {props.item.DetailDesc?.length > 0 && props.item.LotExt === undefined && (
              <LotDetail>
                <LotProperty>Description: </LotProperty>
                {readMore ? (
                  <span>
                    {props.item.DetailDesc}
                    <TextButton onPress={togglereadmore}>Read Less</TextButton>
                  </span>
                ) : (
                  <span>
                    {shortDesc}
                    {compactMode && props.item.DetailDesc.length > 50 && (
                      <span>
                        ... <TextButton onPress={togglereadmore}>Read More</TextButton>
                      </span>
                    )}
                  </span>
                )}
              </LotDetail>
            )}
          </>
        )}
      </Description>
      {props.item.AuctionSessionStatus !== 2
      && props.item.AuctionSessionStatus !== 3
      && auctionStatusId !== 2 ? (
          props.showPricesRealized && props.item.PriceRealized ? (
            <LotProperty>
              <div>Price Realized:</div>
              <LoginToUse overlayText={'Login to view'} hideChildren alwaysShowOverlay>
                <LazyImg src={props.item.PriceRealized} alt='Price obfuscated' />
              </LoginToUse>
            </LotProperty>
          ) : null
        ) : ( !completedLot
        && <AuctionItemActions item={props.item} compact={compactMode === 'true'} />
        )}
    </Item>
  );
};
